<template>
  <TextBase
    v-if="fieldConf.type === 'text'"
    :text="transformedField"
    :type="textOptions?.type ?? 'label'"
    :weight="textOptions?.weight ?? 'regular'"
    class="text-base"
    color="inherit"
    text-wrap="nowrap"
    overflow="ellipsis"
    overflow-tooltip
  />
  <FileSize
    v-else-if="fieldConf.type === 'file-size'"
    :value="transformedField"
    class="text-base"
  />
  <EntityConnectionBadge
    v-else-if="fieldConf.type === 'entity-connection'"
    :entity-connection-path="transformedField"
  />
</template>

<script lang="ts" setup generic="T extends Field">
import TextBase, { TextType, TextWeight } from '@/design-system/atoms/TextBase.vue'
import { DirectoryItem, Field } from './types'
import FileSize from './FileSize.vue'
import EntityConnectionBadge from './EntityConnectionBadge.vue'
import { computed, toRefs } from 'vue'

const fieldTypeMap = {
  name: { type: 'text' },
  description: { type: 'text' },
  fileSize: { type: 'file-size' },
  fileType: { type: 'text' },
  uploadDate: { type: 'text', transform: (v: DirectoryItem) => toDateString(v.createdDate) },
  uploadedBy: { type: 'text' },
  type: {
    type: 'text',
    transform: (value: DirectoryItem) => {
      if (value.type === 'file') {
        const extension = value.name.split('.').pop()
        return extension ?? 'File'
      }
      if (value.type === 'directory') return 'Directory'
      if (value.type === 'link') return 'Link'
    }
  },
  entityConnection: {
    type: 'entity-connection',
    transform: (value: DirectoryItem) => value.entityConnectionPath || undefined
  },
  lastModified: {
    type: 'text',
    transform: (value: DirectoryItem) =>
      value.type === 'file' ? toDateString(value.metadata?.lastModified) : undefined
  },
  width: {
    type: 'text',
    transform: (value: DirectoryItem) => (value.type === 'file' ? value.metadata?.width : undefined)
  },
  height: {
    type: 'text',
    transform: (value: DirectoryItem) =>
      value.type === 'file' ? value.metadata?.height : undefined
  },
  length: {
    type: 'text',
    transform: (value: DirectoryItem) =>
      value.type === 'file' ? value.metadata?.length : undefined
  },
  resolution: {
    type: 'text',
    transform: (value: DirectoryItem) => {
      if (value.type !== 'file' || !value.metadata) return undefined
      const xRes = value.metadata.xResolution || value.metadata.xresolution
      const yRes = value.metadata.yResolution || value.metadata.yresolution
      return xRes && yRes ? `${xRes} x ${yRes}` : undefined
    }
  },
  nrOfPages: {
    type: 'text',
    transform: (value: DirectoryItem) =>
      value.type === 'file' ? value.metadata?.nrOfPages : undefined
  },
  creator: {
    type: 'text',
    transform: (value: DirectoryItem) =>
      value.type === 'file' ? value.metadata?.creator : undefined
  },
  createdDate: {
    type: 'text',
    transform: (value: DirectoryItem) =>
      value.type === 'file' ? toDateString(value.metadata?.createdDate) : undefined
  },
  lastAuthor: {
    type: 'text',
    transform: (value: DirectoryItem) =>
      value.type === 'file' ? value.metadata?.lastAuthor : undefined
  }
} as const

type FieldTypeMap = typeof fieldTypeMap
type FieldType = FieldTypeMap[keyof FieldTypeMap]['type']
type TextOptions = { type?: TextType; weight?: TextWeight }

type FieldOptions<T extends Field> = FieldTypeMap[T]['type'] extends 'text'
  ? TextOptions
  : undefined

type FieldConf = {
  type: FieldType
  transform?: (value: DirectoryItem) => any
}

type Props = {
  item: DirectoryItem
  field: T
  options?: FieldOptions<T> | Record<string, any>
}

const props = defineProps<Props>()

const { item, field, options } = toRefs(props)

const toDateString = (value: string | Date | undefined): string | undefined => {
  if (!value) return undefined
  let date: Date
  if (typeof value === 'string') {
    date = new Date(value)
  } else {
    date = value
  }
  return date.toLocaleString()
}

const fieldConf = computed<FieldConf>(() => fieldTypeMap[field.value])

const textOptions = computed((): TextOptions | undefined => {
  if (fieldConf.value.type !== 'text') {
    return undefined
  }

  if (options?.value) {
    return options.value as TextOptions
  }

  return { type: 'label' }
})

const transformedField = computed(() => {
  if (fieldConf.value.transform) {
    return fieldConf.value.transform(item.value)
  }
  return item.value[field.value as keyof DirectoryItem]
})
</script>
